import { useContext, useEffect, useState } from 'react';
import { Dialog } from '@mui/material';
import cloneDeep from 'lodash/cloneDeep';
import dayjs from 'helpers/dayjs';
// Components
import { AuthContext } from 'context/auth-context';
import { CARD_FORM_ERROR, GET_CARDS, UPDATE_CARDS_METADATA, CLOSE_REVIEW_CARD_FORM_MODAL } from 'context/cards/types';
import { CardContext } from 'context/cards/cardContext';
import CardIdentity from './steps/CardIdentity';
import CardOverview from './steps/CardOverview/CardOverview';
import CustomizeCard from './steps/CustomizeCard';
import OrderingCard from './steps/OrderingCard';
// Services
import { Cards, Overview } from 'lib/api';
// Utils and Helpers
import { REPLENISH_FRECUENCY } from 'helpers/constants';
// Assets
import './ReviewCard.css';

export const CARD_FORM_STEP = {
    CUSTOMIZE: 'CUSTOMIZE',
    IDENTITY: 'IDENTITY',
    ORDERING: 'ORDERING',
    OVERVIEW: 'OVERVIEW'
};
const CARD_VALUES_TO_CREATE = {
    type: '',
    cardholderId: null,
    label: '',
    limitAmount: 0,
    memo: '',
    billingAddress: {
        line1: '',
        line2: '',
        city: '',
        state: '',
        country: '',
        postalCode: ''
    },
    autoLock: '',
    has_manual_replenish: REPLENISH_FRECUENCY.ONE_TIME
};
const ReviewCard = () => {
    const { cards, dispatch, isReviewCardModalOpen, isFormSubmitting, cardRequestToReview } = useContext(CardContext);
    const { businessInfo } = useContext(AuthContext);
    const [cardForm, setCardForm] = useState(cardRequestToReview);
    const [formStep, setFormStep] = useState(CARD_FORM_STEP.CUSTOMIZE);
    const [createdCard, setCreatedCard] = useState();
    const { address } = businessInfo;

    useEffect(() => {
        setCardForm(cardRequestToReview);
    }, [cardRequestToReview]);

    const handleCardFormModalClose = (event, reason) => {
        if (isFormSubmitting) return;

        dispatch({ type: CLOSE_REVIEW_CARD_FORM_MODAL });
        setFormStep(CARD_FORM_STEP.CUSTOMIZE);
        setCardForm(cardRequestToReview);
    };

    const handleSubmit = async (values, actions) => {
        setCardForm({
            ...cardForm,
            ...values
        });
        setFormStep(CARD_FORM_STEP.ORDERING);
        try {
            await createCard(values);
            setFormStep(CARD_FORM_STEP.OVERVIEW);
        } catch (error) {
            setFormStep(CARD_FORM_STEP.IDENTITY);
            dispatch({
                type: CARD_FORM_ERROR,
                payload: error?.response?.data?.message
            });
        }
    };
    const createCard = async values => {
        const body = mapValuesToCreateCardModel(values);
        const cardCreated = await Cards.createCard(body);
        setCreatedCard(cardCreated);
        dispatch({ type: GET_CARDS });

        const overviewInfo = await Overview.getOverviewInfo();
        dispatch({
            type: UPDATE_CARDS_METADATA,
            payload: overviewInfo
        });

        // Push to Google tag manager
        window.dataLayer.push({
            event: cards.length > 0 ? 'virtual-card-created' : 'first-virtual-card-created'
        });
    };
    const mapValuesToCreateCardModel = values => {
        const cardValues = cloneDeep(values);
        const body = {};
        for (const key of Object.keys(cardValues)) {
            if (key in CARD_VALUES_TO_CREATE) {
                body[key] = cardValues[key];
                if (key === 'autoLock' && cardValues[key]) {
                    body[key] = dayjs(cardValues[key]).tz('America/Los_Angeles').startOf('day').toISOString();
                }
                if (key === 'has_manual_replenish') {
                    body[key] = cardValues[key] ? true : false;
                }
            }
        }
        body.cardholderId = values.id_user;
        body.billingAddress = address;
        body.cardRequestId = cardRequestToReview.id;
        delete body.billingAddress.addressType;
        return body;
    };

    const handleBackClick = currentStep => {
        setFormStep(currentStep);
    };

    const handleStepSubmit = (values, actions) => {
        setCardForm({
            ...cardForm,
            ...values
        });
        setFormStep(getNextStep());
    };

    const getNextStep = () => {
        if (formStep === CARD_FORM_STEP.CUSTOMIZE) return CARD_FORM_STEP.IDENTITY;
        if (formStep === CARD_FORM_STEP.IDENTITY) return CARD_FORM_STEP.ORDERING;
        if (formStep === CARD_FORM_STEP.ORDERING) return CARD_FORM_STEP.OVERVIEW;
    };

    const handleDoneClick = () => {
        setCardForm(cardRequestToReview);
        setFormStep(CARD_FORM_STEP.CUSTOMIZE);
        dispatch({ type: CLOSE_REVIEW_CARD_FORM_MODAL });
    };

    return (
        <Dialog open={isReviewCardModalOpen} onClose={() => {}} fullWidth maxWidth="md" sx={{ zIndex: 2 }}>
            {formStep === CARD_FORM_STEP.CUSTOMIZE && (
                <CustomizeCard
                    card={cardForm}
                    onBackBtnClick={handleBackClick}
                    onStepSubmit={handleStepSubmit}
                    onCardModalClose={handleCardFormModalClose}
                />
            )}
            {formStep === CARD_FORM_STEP.IDENTITY && (
                <CardIdentity
                    card={cardForm}
                    onBackBtnClick={handleBackClick}
                    onOrderCardClick={handleSubmit}
                    onCardModalClose={handleCardFormModalClose}
                />
            )}
            {formStep === CARD_FORM_STEP.ORDERING && <OrderingCard card={cardForm} />}
            {formStep === CARD_FORM_STEP.OVERVIEW && (
                <CardOverview createdCard={createdCard} card={cardForm} onDoneClick={handleDoneClick} />
            )}
        </Dialog>
    );
};

export default ReviewCard;
